import { Currency } from '@models/Currency'

export function currencyFormat(
  num?: number,
  fromCts: boolean = false,
  showCents: boolean = true,
  hideCurrency: boolean = false,
  currencySymbol: string = '€'
): string {
  if (typeof num !== 'number') return '--'

  const number = fromCts ? num / 100 : num
  const formatted = showCents
    ? number.toFixed(2)
    : Math.round(number).toString()
  const result = formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return hideCurrency ? result : `${result} ${currencySymbol}`
}

export function numberFormat(number: number): string {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
}

/**
 * Analytics currency formatting matching the membership analytics Figma
 * (799-904, 798-3847): French style, space thousands separator. Input is in
 * cents. Rounded by default - `18640000` -> "186 400 €" - or with cents for
 * transaction amounts: `4050` -> "40,50 €".
 *
 * `currency` is the lite embed (`{ isoCode, symbol }`) the analytics payloads
 * carry: on the plan for table rows, on the statistics envelope for aggregates.
 * Its symbol is appended like `currencyFormat` does, falling back to the ISO
 * code when the backend has none on file.
 *
 * Omitted or `null` means the currency is *unknown*, which is not the same as
 * euro: the figure renders bare, with the French grouping but no symbol at all.
 * Printing "€" there used to make a dollar program read as a euro one, and a
 * wrong symbol misleads where a missing one merely withholds.
 */
export function analyticsCurrency(
  cents?: number | null,
  showCents = false,
  currency?: Pick<Currency, 'isoCode' | 'symbol'> | null
): string {
  if (typeof cents !== 'number') return '--'

  const fractionDigits = showCents ? 2 : 0

  const amount = new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(cents / 100)

  if (!currency) return amount

  // No-break space before the symbol, exactly where `fr-FR`'s own currency
  // style puts it, so a euro amount keeps its familiar spacing.
  return `${amount}\u00A0${currency.symbol || currency.isoCode}`
}

/**
 * Resolve a currency symbol from an embedded Currency object. Programs expose
 * their currency as an id once normalized, but as a full object when nested
 * (e.g. subscription.plan.program.currency); only the latter carries a symbol.
 */
export function currencySymbolOf(
  currency?: number | Currency | null
): string | undefined {
  return currency && typeof currency === 'object' ? currency.symbol : undefined
}
